import React from "react";
import { useNavigate } from "react-router-dom";
import { GUEST_EMAIL, enquiryOptions, topics, timeSlots } from "./homeData/contactData";
import { getTomorrow, isWeekend, nextWeekday } from "./homeData/homeConstants";

// ─── WEEKDAY DATE INPUT ───────────────────────────────────────────────────────
function WeekdayDateInput({ value, onChange, className, min, id, name }) {
  const handleChange = (e) => {
    const val = e.target.value;
    onChange(isWeekend(val) ? nextWeekday(val) : val);
  };

  return (
    <div className="home-contact__date-wrap">
      <input
        type="date"
        id={id}
        name={name}
        className={className}
        min={min || getTomorrow()}
        value={value}
        onChange={handleChange}
      />
      {value && isWeekend(value) && (
        <span className="home-contact__date-warn">
          Weekends unavailable — moved to next weekday.
        </span>
      )}
    </div>
  );
}

// ─── CONTACT US ───────────────────────────────────────────────────────────────
export function ContactUs() {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState("message");

  const [form,      setForm]      = React.useState({ name: "", email: "", phone: "", destination: "", message: "", enquiry: "" });
  const [errors,    setErrors]    = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const [sending,   setSending]   = React.useState(false);

  const validateMsg = () => {
    const e = {};
    if (!form.name.trim())  e.name    = "Name is required";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone   = "Phone number is required";
    if (!form.enquiry)      e.enquiry = "Please select an enquiry type";
    if (form.message.trim().length < 10) e.message = "Write at least 10 characters";
    return e;
  };

  const handleMsgSubmit = () => {
    const e = validateMsg();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSubmitted(true);
    }, 1200);
  };

  const [meet,          setMeet]          = React.useState({ name: "", email: "", phone: "", date: "", time: "", topic: "", notes: "" });
  const [meetErrors,    setMeetErrors]    = React.useState({});
  const [meetScheduled, setMeetScheduled] = React.useState(false);
  const [meetLink,      setMeetLink]      = React.useState("");

  const tomorrow = getTomorrow();

  const validateMeet = () => {
    const e = {};
    if (!meet.name.trim())  e.name  = "Name is required";
    if (!meet.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Valid email required";
    if (!meet.phone.trim()) e.phone = "Phone number is required";
    if (!meet.date)  e.date  = "Select a date";
    if (!meet.time)  e.time  = "Select a time slot";
    if (!meet.topic) e.topic = "Select a meeting topic";
    return e;
  };

  const handleSchedule = () => {
    const e = validateMeet();
    if (Object.keys(e).length) { setMeetErrors(e); return; }

    const [year, month, day] = meet.date.split("-").map(Number);
    const [hm, ampm] = meet.time.split(" ");
    let [hh, mm] = hm.split(":").map(Number);
    if (ampm === "PM" && hh !== 12) hh += 12;
    if (ampm === "AM" && hh === 12) hh = 0;
    const pad = n => String(n).padStart(2, "0");
    const startDT = `${year}${pad(month)}${pad(day)}T${pad(hh)}${pad(mm)}00`;
    const endDate = new Date(year, month - 1, day, hh, mm + 45);
    const endDT   = `${endDate.getFullYear()}${pad(endDate.getMonth() + 1)}${pad(endDate.getDate())}T${pad(endDate.getHours())}${pad(endDate.getMinutes())}00`;

    const title   = encodeURIComponent(`Visa Consultation – ${meet.name}`);
    const details = encodeURIComponent(`Visa Consultation\n\nClient: ${meet.name}\nPhone: ${meet.phone}\nTopic: ${meet.topic}${meet.notes ? `\nNotes: ${meet.notes}` : ""}\n\nScheduled via Visa Expert`);
    const guests  = encodeURIComponent(`${meet.email},${GUEST_EMAIL}`);
    const calURL  = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDT}/${endDT}&details=${details}&add=${guests}&sf=true&output=xml`;

    setMeetLink(calURL);
    setMeetScheduled(true);
    setTimeout(() => window.open(calURL, "_blank"), 500);
  };

  const channels = [
    {
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>),
      label: "Email Us", value: GUEST_EMAIL,
      color: "#0ea5e9", bg: "rgba(14,165,233,.08)", border: "rgba(14,165,233,.2)",
    },
  ];

  return (
    <section className="home-contact" id="contact">
      <div className="home-contact__bg" aria-hidden="true">
        <div className="home-contact__orb home-contact__orb--1" />
        <div className="home-contact__orb home-contact__orb--2" />
        <div className="home-contact__grid-bg" />
      </div>

      <div className="home-contact__inner">

        <div className="home-contact__head" data-aos="fade-down">
          <h2 className="home-contact__title">We're here to help</h2>
          <p className="home-contact__sub">Reach out via email or book a free Google Meet consultation.</p>
        </div>

        <div className="home-contact__channels-row" data-aos="fade-right" data-aos-delay="60">
          {channels.map((ch, i) => (
            <div key={i} className="home-contact__channel" style={{ "--ch-color": ch.color, "--ch-bg": ch.bg }}>
              <div className="home-contact__channel-icon">{ch.icon}</div>
              <div className="home-contact__channel-body">
                <div className="home-contact__channel-label">{ch.label}</div>
                <div className="home-contact__channel-value">{ch.value}</div>
                <div className="home-contact__channel-sub">{ch.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="home-contact__tabs" data-aos="fade-left" data-aos-delay="80">
          <button className={`home-contact__tab${tab === "message" ? " home-contact__tab--active" : ""}`} onClick={() => setTab("message")}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            Send a Message
          </button>
          <button className="home-contact__tab" onClick={() => navigate("/schedule-meeting")}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Schedule Meeting
          </button>
        </div>

        {tab === "message" && (
          <div className="home-contact__panel" data-aos="fade-up" data-aos-delay="100">
            {submitted ? (
              <div className="home-contact__success">
                <div className="home-contact__success-ring">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <h3 className="home-contact__success-title">Message sent!</h3>
                <p className="home-contact__success-sub">We'll reply within 2 hours on working days.</p>
                <button className="home-contact__submit home-contact__submit--ghost" onClick={() => { setForm({ name: "", email: "", phone: "", destination: "", message: "", enquiry: "" }); setSubmitted(false); setErrors({}); }}>
                  Send another message
                </button>
              </div>
            ) : (
              <div className="home-contact__form" data-aos="fade-right" data-aos-delay="140">
                <div className="home-contact__form-header">
                  <h3 className="home-contact__form-title">Send us a message</h3>
                  <p className="home-contact__form-note">We reply to every message within 2 hours on working days.</p>
                </div>

                <div className="home-contact__field">
                  <label className="home-contact__label" htmlFor="contact-enquiry">Enquiry Type *</label>
                  <div className="home-contact__select-wrap">
                    <select
                      id="contact-enquiry"
                      name="contact-enquiry"
                      className={`home-contact__select${errors.enquiry ? " home-contact__input--err" : ""}`}
                      value={form.enquiry}
                      onChange={e => { setForm(p => ({ ...p, enquiry: e.target.value })); setErrors(p => ({ ...p, enquiry: "" })); }}
                    >
                      {enquiryOptions.map(o => <option key={o.value} value={o.value} disabled={o.value === ""} hidden={o.value === "" && form.enquiry !== "" ? false : undefined}>{o.label}</option>)}
                    </select>
                    <span className="home-contact__select-arrow"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>
                  </div>
                  {errors.enquiry && <span className="home-contact__err">{errors.enquiry}</span>}
                </div>

                <div className="home-contact__row">
                  <div className="home-contact__field">
                    <label className="home-contact__label" htmlFor="contact-name">Full Name *</label>
                    <input
                      id="contact-name" name="contact-name"
                      className={`home-contact__input${errors.name ? " home-contact__input--err" : ""}`}
                      placeholder="e.g. Priya Sharma" value={form.name}
                      onChange={e => { setForm(p => ({ ...p, name: e.target.value })); setErrors(p => ({ ...p, name: "" })); }}
                    />
                    {errors.name && <span className="home-contact__err">{errors.name}</span>}
                  </div>
                  <div className="home-contact__field">
                    <label className="home-contact__label" htmlFor="contact-email">Email Address *</label>
                    <input
                      id="contact-email" name="contact-email" type="email"
                      className={`home-contact__input${errors.email ? " home-contact__input--err" : ""}`}
                      placeholder="you@example.com" value={form.email}
                      onChange={e => { setForm(p => ({ ...p, email: e.target.value })); setErrors(p => ({ ...p, email: "" })); }}
                    />
                    {errors.email && <span className="home-contact__err">{errors.email}</span>}
                  </div>
                </div>

                <div className="home-contact__row">
                  <div className="home-contact__field">
                    <label className="home-contact__label" htmlFor="contact-phone">Phone Number *</label>
                    <input
                      id="contact-phone" name="contact-phone"
                      className={`home-contact__input${errors.phone ? " home-contact__input--err" : ""}`}
                      placeholder="+91 98765 43210" value={form.phone}
                      onChange={e => { setForm(p => ({ ...p, phone: e.target.value })); setErrors(p => ({ ...p, phone: "" })); }}
                    />
                    {errors.phone && <span className="home-contact__err">{errors.phone}</span>}
                  </div>
                  <div className="home-contact__field">
                    <label className="home-contact__label" htmlFor="contact-destination">Destination Country</label>
                    <input
                      id="contact-destination" name="contact-destination"
                      className="home-contact__input" placeholder="e.g. Japan, UAE, UK…"
                      value={form.destination}
                      onChange={e => setForm(p => ({ ...p, destination: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="home-contact__field">
                  <label className="home-contact__label" htmlFor="contact-message">Your Message *</label>
                  <textarea
                    id="contact-message" name="contact-message"
                    className={`home-contact__textarea${errors.message ? " home-contact__input--err" : ""}`}
                    placeholder="Describe your visa requirements, travel dates, or concerns…"
                    rows={4} value={form.message}
                    onChange={e => { setForm(p => ({ ...p, message: e.target.value })); setErrors(p => ({ ...p, message: "" })); }}
                  />
                  {errors.message && <span className="home-contact__err">{errors.message}</span>}
                </div>

                <button className={`home-contact__submit${sending ? " home-contact__submit--sending" : ""}`} onClick={handleMsgSubmit} disabled={sending}>
                  {sending
                    ? (<><span className="home-contact__spinner"/> Sending…</>)
                    : (<><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>Send Message</>)
                  }
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "meeting" && (
          <div className="home-contact__panel home-contact__panel--meet" data-aos="fade-up" data-aos-delay="100">
            {meetScheduled ? (
              <div className="home-contact__success">
                <div className="home-contact__success-ring home-contact__success-ring--green">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <h3 className="home-contact__success-title">Meeting Scheduled! 🎉</h3>
                <p className="home-contact__success-sub">
                  Google Calendar opened in a new tab. Once you confirm, a Meet link is sent to <strong>{meet.email}</strong> and <strong>{GUEST_EMAIL}</strong>.
                </p>
                <a href={meetLink} target="_blank" rel="noopener noreferrer" className="home-contact__submit home-contact__cal-link">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  Open Google Calendar Again
                </a>
                <button className="home-contact__submit home-contact__submit--ghost" onClick={() => { setMeet({ name: "", email: "", phone: "", date: "", time: "", topic: "", notes: "" }); setMeetScheduled(false); setMeetErrors({}); }}>
                  Schedule another meeting
                </button>
              </div>
            ) : (
              <div className="home-contact__form" data-aos="fade-left" data-aos-delay="140">
                <div className="home-contact__form-header">
                  <div className="home-contact__meet-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/></svg>
                    Google Meet · 15 min · Free
                  </div>
                  <h3 className="home-contact__form-title">Schedule a Consultation</h3>
                  <p className="home-contact__form-note">Pick a slot — a Google Meet invite is sent to you and our expert automatically.</p>
                </div>

                <div className="home-contact__row">
                  <div className="home-contact__field">
                    <label className="home-contact__label" htmlFor="meet-name">Full Name *</label>
                    <input id="meet-name" name="meet-name"
                      className={`home-contact__input${meetErrors.name ? " home-contact__input--err" : ""}`}
                      placeholder="e.g. Arjun Kapoor" value={meet.name}
                      onChange={e => { setMeet(p => ({ ...p, name: e.target.value })); setMeetErrors(p => ({ ...p, name: "" })); }}
                    />
                    {meetErrors.name && <span className="home-contact__err">{meetErrors.name}</span>}
                  </div>
                  <div className="home-contact__field">
                    <label className="home-contact__label" htmlFor="meet-email">Email Address *</label>
                    <input id="meet-email" name="meet-email" type="email"
                      className={`home-contact__input${meetErrors.email ? " home-contact__input--err" : ""}`}
                      placeholder="you@example.com" value={meet.email}
                      onChange={e => { setMeet(p => ({ ...p, email: e.target.value })); setMeetErrors(p => ({ ...p, email: "" })); }}
                    />
                    {meetErrors.email && <span className="home-contact__err">{meetErrors.email}</span>}
                  </div>
                </div>

                <div className="home-contact__field">
                  <label className="home-contact__label" htmlFor="meet-phone">Phone Number *</label>
                  <input id="meet-phone" name="meet-phone"
                    className={`home-contact__input${meetErrors.phone ? " home-contact__input--err" : ""}`}
                    placeholder="+91 98765 43210" value={meet.phone}
                    onChange={e => { setMeet(p => ({ ...p, phone: e.target.value })); setMeetErrors(p => ({ ...p, phone: "" })); }}
                  />
                  {meetErrors.phone && <span className="home-contact__err">{meetErrors.phone}</span>}
                </div>

                <div className="home-contact__field">
                  <label className="home-contact__label" htmlFor="meet-topic">Meeting Topic *</label>
                  <div className="home-contact__select-wrap">
                    <select id="meet-topic" name="meet-topic"
                      className={`home-contact__select${meetErrors.topic ? " home-contact__input--err" : ""}`}
                      value={meet.topic}
                      onChange={e => { setMeet(p => ({ ...p, topic: e.target.value })); setMeetErrors(p => ({ ...p, topic: "" })); }}
                    >
                      <option value="">Select a topic…</option>
                      {topics.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <span className="home-contact__select-arrow"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>
                  </div>
                  {meetErrors.topic && <span className="home-contact__err">{meetErrors.topic}</span>}
                </div>

                <div className="home-contact__row">
                  <div className="home-contact__field">
                    <label className="home-contact__label" htmlFor="meet-date">Preferred Date *</label>
                    <WeekdayDateInput
                      id="meet-date" name="meet-date"
                      className={`home-contact__input${meetErrors.date ? " home-contact__input--err" : ""}`}
                      min={tomorrow} value={meet.date}
                      onChange={val => { setMeet(p => ({ ...p, date: val })); setMeetErrors(p => ({ ...p, date: "" })); }}
                    />
                    {meetErrors.date && <span className="home-contact__err">{meetErrors.date}</span>}
                  </div>
                  <div className="home-contact__field">
                    <label className="home-contact__label" htmlFor="meet-time">Time Slot *</label>
                    <div className="home-contact__select-wrap">
                      <select id="meet-time" name="meet-time"
                        className={`home-contact__select${meetErrors.time ? " home-contact__input--err" : ""}`}
                        value={meet.time}
                        onChange={e => { setMeet(p => ({ ...p, time: e.target.value })); setMeetErrors(p => ({ ...p, time: "" })); }}
                      >
                        <option value="">Choose a slot…</option>
                        {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <span className="home-contact__select-arrow"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>
                    </div>
                    {meetErrors.time && <span className="home-contact__err">{meetErrors.time}</span>}
                  </div>
                </div>

                {meet.date && (
                  <div className="home-contact__timegrid">
                    <span className="home-contact__timegrid-label">Quick select time:</span>
                    <div className="home-contact__timegrid-slots">
                      {timeSlots.map(t => (
                        <button key={t} className={`home-contact__timeslot${meet.time === t ? " home-contact__timeslot--active" : ""}`}
                          onClick={() => { setMeet(p => ({ ...p, time: t })); setMeetErrors(p => ({ ...p, time: "" })); }}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="home-contact__field">
                  <label className="home-contact__label" htmlFor="meet-notes">
                    Additional Notes <span className="home-contact__label-opt">(optional)</span>
                  </label>
                  <textarea id="meet-notes" name="meet-notes"
                    className="home-contact__textarea"
                    placeholder="Any specific questions before the call…"
                    rows={3} value={meet.notes}
                    onChange={e => setMeet(p => ({ ...p, notes: e.target.value }))}
                  />
                </div>

               {/*} <div className="home-contact__guest-notice">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                  Invite will be sent to <strong>{meet.email || "your email"}</strong> and our team at <strong>{GUEST_EMAIL}</strong>
                </div>*/}

                <button className="home-contact__submit home-contact__submit--meet" onClick={handleSchedule}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  Schedule Google Meet
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </section>
  );
}

// ─── SCHEDULE MEETING FLOAT ───────────────────────────────────────────────────
export function ScheduleMeetingFloat() {
  const [open,          setOpen]          = React.useState(false);
  const [meetScheduled, setMeetScheduled] = React.useState(false);
  const [meetLink,      setMeetLink]      = React.useState("");

  const [meet,       setMeet]       = React.useState({ name: "", email: "", phone: "", date: "", time: "", topic: "", notes: "" });
  const [meetErrors, setMeetErrors] = React.useState({});

  const tomorrow = getTomorrow();

  const validateMeet = () => {
    const e = {};
    if (!meet.name.trim())  e.name  = "Name is required";
    if (!meet.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Valid email required";
    if (!meet.phone.trim()) e.phone = "Phone number is required";
    if (!meet.date)  e.date  = "Select a date";
    if (!meet.time)  e.time  = "Select a time slot";
    if (!meet.topic) e.topic = "Select a meeting topic";
    return e;
  };

  const handleSchedule = () => {
    const e = validateMeet();
    if (Object.keys(e).length) { setMeetErrors(e); return; }

    const [year, month, day] = meet.date.split("-").map(Number);
    const [hm, ampm] = meet.time.split(" ");
    let [hh, mm] = hm.split(":").map(Number);
    if (ampm === "PM" && hh !== 12) hh += 12;
    if (ampm === "AM" && hh === 12) hh = 0;
    const pad = n => String(n).padStart(2, "0");
    const startDT = `${year}${pad(month)}${pad(day)}T${pad(hh)}${pad(mm)}00`;
    const endDate = new Date(year, month - 1, day, hh, mm + 45);
    const endDT   = `${endDate.getFullYear()}${pad(endDate.getMonth() + 1)}${pad(endDate.getDate())}T${pad(endDate.getHours())}${pad(endDate.getMinutes())}00`;

    const title   = encodeURIComponent(`Visa Consultation – ${meet.name}`);
    const details = encodeURIComponent(`Visa Consultation Meeting\n\nClient: ${meet.name}\nPhone: ${meet.phone}\nEmail: ${meet.email}\nTopic: ${meet.topic}${meet.notes ? `\nNotes: ${meet.notes}` : ""}\n\nScheduled via Visa Expert`);
    const guests  = encodeURIComponent(`${meet.email},${GUEST_EMAIL}`);
    const calURL  = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDT}/${endDT}&details=${details}&add=${guests}&sf=true&output=xml`;

    setMeetLink(calURL);
    setMeetScheduled(true);
    setTimeout(() => window.open(calURL, "_blank"), 500);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setMeet({ name: "", email: "", phone: "", date: "", time: "", topic: "", notes: "" });
      setMeetErrors({});
      setMeetScheduled(false);
      setMeetLink("");
    }, 350);
  };

  return (
    <>
      {open && <div className="home-smf__overlay" onClick={handleClose} />}

      <button
        className={`home-smf__fab${open ? " home-smf__fab--open" : ""}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Schedule a Meeting"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        )}
        <span className="home-smf__fab-label">{open ? "Close" : "Schedule Meeting"}</span>
      </button>

      <div className={`home-smf__card${open ? " home-smf__card--open" : ""}`}>

        <div className="home-smf__card-header">
          <div className="home-smf__card-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/>
            </svg>
            Google Meet · 15 min · Free
          </div>
          <h3 className="home-smf__card-title">Schedule a Consultation</h3>
          <p className="home-smf__card-sub">Book a free slot with our visa expert.</p>
        </div>

        <div className="home-smf__card-body">
          {meetScheduled ? (
            <div className="home-smf__success">
              <div className="home-smf__success-ring">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h4 className="home-smf__success-title">Meeting Scheduled! 🎉</h4>
              <p className="home-smf__success-sub">
                Google Calendar opened in a new tab. Once confirmed, the Meet link is sent to <strong>{meet.email}</strong> and our team.
              </p>
              <a href={meetLink} target="_blank" rel="noopener noreferrer" className="home-smf__btn home-smf__btn--meet home-smf__btn--no-dec">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Open Google Calendar Again
              </a>
              <button className="home-smf__btn home-smf__btn--ghost" onClick={() => { setMeet({ name: "", email: "", phone: "", date: "", time: "", topic: "", notes: "" }); setMeetScheduled(false); setMeetErrors({}); }}>
                Schedule another
              </button>
            </div>
          ) : (
            <div className="home-smf__form">

              <div className="home-smf__field">
                <label className="home-smf__label" htmlFor="smf-name">Full Name *</label>
                <input id="smf-name" name="smf-name"
                  className={`home-smf__input${meetErrors.name ? " home-smf__input--err" : ""}`}
                  placeholder="e.g. Priya Sharma" value={meet.name}
                  onChange={e => { setMeet(p => ({ ...p, name: e.target.value })); setMeetErrors(p => ({ ...p, name: "" })); }}
                />
                {meetErrors.name && <span className="home-smf__err">{meetErrors.name}</span>}
              </div>

              <div className="home-smf__field">
                <label className="home-smf__label" htmlFor="smf-email">Email Address *</label>
                <input id="smf-email" name="smf-email" type="email"
                  className={`home-smf__input${meetErrors.email ? " home-smf__input--err" : ""}`}
                  placeholder="you@example.com" value={meet.email}
                  onChange={e => { setMeet(p => ({ ...p, email: e.target.value })); setMeetErrors(p => ({ ...p, email: "" })); }}
                />
                {meetErrors.email && <span className="home-smf__err">{meetErrors.email}</span>}
              </div>

              <div className="home-smf__field">
                <label className="home-smf__label" htmlFor="smf-phone">Phone Number *</label>
                <input id="smf-phone" name="smf-phone"
                  className={`home-smf__input${meetErrors.phone ? " home-smf__input--err" : ""}`}
                  placeholder="+91 98765 43210" value={meet.phone}
                  onChange={e => { setMeet(p => ({ ...p, phone: e.target.value })); setMeetErrors(p => ({ ...p, phone: "" })); }}
                />
                {meetErrors.phone && <span className="home-smf__err">{meetErrors.phone}</span>}
              </div>

              <div className="home-smf__field">
                <label className="home-smf__label" htmlFor="smf-topic">Meeting Topic *</label>
                <div className="home-smf__select-wrap">
                  <select id="smf-topic" name="smf-topic"
                    className={`home-smf__input home-smf__select${meetErrors.topic ? " home-smf__input--err" : ""}`}
                    value={meet.topic}
                    onChange={e => { setMeet(p => ({ ...p, topic: e.target.value })); setMeetErrors(p => ({ ...p, topic: "" })); }}
                  >
                    <option value="">Select a topic…</option>
                    {topics.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <span className="home-smf__select-arrow">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </div>
                {meetErrors.topic && <span className="home-smf__err">{meetErrors.topic}</span>}
              </div>

              <div className="home-smf__field">
                <label className="home-smf__label" htmlFor="smf-date">Preferred Date *</label>
                <WeekdayDateInput
                  id="smf-date" name="smf-date"
                  className={`home-smf__input${meetErrors.date ? " home-smf__input--err" : ""}`}
                  min={tomorrow} value={meet.date}
                  onChange={val => { setMeet(p => ({ ...p, date: val })); setMeetErrors(p => ({ ...p, date: "" })); }}
                />
                {meetErrors.date && <span className="home-smf__err">{meetErrors.date}</span>}
              </div>

              <div className="home-smf__field">
                <label className="home-smf__label" htmlFor="smf-time">Time Slot *</label>
                <div className="home-smf__select-wrap">
                  <select id="smf-time" name="smf-time"
                    className={`home-smf__input home-smf__select${meetErrors.time ? " home-smf__input--err" : ""}`}
                    value={meet.time}
                    onChange={e => { setMeet(p => ({ ...p, time: e.target.value })); setMeetErrors(p => ({ ...p, time: "" })); }}
                  >
                    <option value="">Choose a slot…</option>
                    {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <span className="home-smf__select-arrow">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </div>
                {meetErrors.time && <span className="home-smf__err">{meetErrors.time}</span>}
              </div>

              {meet.date && (
                <div className="home-smf__timegrid">
                  <span className="home-smf__timegrid-label">Quick select:</span>
                  <div className="home-smf__timegrid-slots">
                    {timeSlots.map(t => (
                      <button key={t} className={`home-smf__timeslot${meet.time === t ? " home-smf__timeslot--active" : ""}`}
                        onClick={() => { setMeet(p => ({ ...p, time: t })); setMeetErrors(p => ({ ...p, time: "" })); }}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="home-smf__field">
                <label className="home-smf__label" htmlFor="smf-notes">
                  Notes <span className="home-smf__optional">(optional)</span>
                </label>
                <textarea id="smf-notes" name="smf-notes"
                  className="home-smf__input home-smf__textarea"
                  placeholder="Any questions before the call…"
                  rows={2} value={meet.notes}
                  onChange={e => setMeet(p => ({ ...p, notes: e.target.value }))}
                />
              </div>

              {/*<div className="home-smf__guest-notice">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                Invite sent to <strong>{meet.email || "your email"}</strong> &amp; <strong>{GUEST_EMAIL}</strong>
              </div>*/}

              <button className="home-smf__btn home-smf__btn--meet" onClick={handleSchedule}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Schedule Google Meet
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
